import './styles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Form, Input, Row, Select } from 'antd'
import { clsx } from 'clsx'

import B2becCustomSelect from '@/components/B2becCustomSelect'
import B2becSpinner from '@/components/B2becSpinner'
import CustomButton from '@/components/CustomButton'
import { NotificationManager } from '@/components/Notification'
import { fapiUserService } from '@/services/fapi'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { getIsLoading, getSubmitStatus } from '@/store/selectors/contactSelector'
import { contactActions } from '@/store/slices/contactSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useAsync from '@/hooks/useAsync'
import { ACTION_STATUS, ASYNC_STATUS, STATUS_CODE } from '@/libs/constants'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import contactReasonsFromPhrase from '../ContactReasonManagement/contactReasonsFromPhrase'

const { Option } = Select
const { TextArea } = Input

const compare = (contactReason1, contactReason2) => {
  const contactReasonNumberA = Number(contactReason1.indexNumber)
  const contactReasonNumberB = Number(contactReason2.indexNumber)
  if (contactReasonNumberA < contactReasonNumberB) {
    return -1
  }
  if (contactReasonNumberA > contactReasonNumberB) {
    return 1
  }
  return 0
}

const ContactFormPage = () => {
  const [contactReasonsFromApi, setContactReasonsFromApi] = useState([])

  const [form] = Form.useForm()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { setPageInfoData } = useAdobeAnalysis()

  const isLoading = useAppSelector(getIsLoading)
  const submitStatus = useAppSelector(getSubmitStatus)

  const getAllContactReasonsFromApi = useCallback(() => {
    return fapiUserService.getContactReasonList(10, 1)
  }, [])

  const onGetApiSuccess = useCallback((response) => {
    if (response?.status === STATUS_CODE.SUCCESS) {
      setContactReasonsFromApi(response?.data)
    }
  }, [])

  const onGetApiError = useCallback((error) => {
    NotificationManager.error({
      message: 'notification.error.getAllContactReasons',
      description: error?.message,
    })
  }, [])

  const { status: getAllContactReasonsApiStatus } = useAsync(
    getAllContactReasonsFromApi,
    true,
    onGetApiSuccess,
    onGetApiError,
  )

  const finalContactReasonList = useMemo(() => {
    if (contactReasonsFromApi.length > 0) {
      const mappedArray = contactReasonsFromApi.map((reason) => {
        const foundItem = contactReasonsFromPhrase.find(
          (phraseItem) => phraseItem?.key?.id === reason?.contactReasonId,
        )
        return {
          ...reason,
          translationContent: t(foundItem?.key?.name || 'N/A'),
        }
      })
      return mappedArray.sort(compare)
    }
    return []
  }, [contactReasonsFromApi, t])

  const isLoadingContactReasons = getAllContactReasonsApiStatus === ASYNC_STATUS.PENDING

  useEffect(() => {
    const contactStatus = submitStatus === ACTION_STATUS.DEFAULT ? 'start' : 'complete'

    setPageInfoData({
      pageName: `contact ${contactStatus}`,
      pageType: PAGE_TYPES.CONTACT,
      pageId: '8',
    })
  }, [submitStatus, setPageInfoData])

  useEffect(() => {
    return function cleanup() {
      dispatch(contactActions.resetContact())
    }
  }, [dispatch])

  const renderOptions = () =>
    finalContactReasonList.map((reason) => (
      <Option
        key={reason.contactReasonId}
        value={`${reason.contactReasonId}-${reason.translationContent}`}
      >
        {reason.translationContent}
      </Option>
    ))

  const handleSubmit = (values) => {
    const { contactReasonIdName, message } = values
    const contactReasonObject = contactReasonIdName.split('-')
    const contactReasonId = contactReasonObject[0]
    const contactReasonName = contactReasonObject[1]
    form.resetFields()
    dispatch(
      contactActions.submitContactRequest({
        contactReasonId,
        contactReasonName,
        message,
      }),
    )
  }

  const isDisabledSubmittingForm = isLoadingContactReasons || !isValidArray(contactReasonsFromApi)

  const renderSubmitForm = () => (
    <>
      <B2becSpinner isLoading={isLoading} />
      <div className="container">
        <div className="contact-form__wrapper mt-6">
          <h1 className="contact-form__title fontBold mb-4">
            {t('contactForm.title')}
          </h1>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              className="custom-form-item"
              label={t('contactForm.reason')}
              name="contactReasonIdName"
              rules={[
                {
                  required: true,
                  message: `${t('contactForm.validation.reasonIsRequired')}`,
                },
              ]}
            >
              <B2becCustomSelect
                placeholder={isLoadingContactReasons
                  ? t('form.placeHolder.loading')
                  : t('contactForm.pleaseSelectTheReason')}
              >
                {renderOptions()}
              </B2becCustomSelect>
            </Form.Item>

            <Form.Item
              className="custom-form-item"
              label={t('contactForm.message')}
              name="message"
              rules={[
                {
                  required: true,
                  message: `${t('contactForm.validation.messageIsRequired')}`,
                },
              ]}
            >
              <TextArea
                placeholder={t('contactForm.message')}
                rows={4}
                style={{ resize: 'none' }}
              />
            </Form.Item>
            <Row gutter={[50]}>
              <Col span={24}>
                <CustomButton
                  className="btn btn--submit"
                  disabled={isDisabledSubmittingForm}
                  htmlType="submit"
                  style={{ marginLeft: 'auto' }}
                  type="primary"
                >
                  {t('contactForm.makeRequest')}
                </CustomButton>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </>
  )

  const renderSubmitResult = () => (
    <div className="contact-form__wrapper mt-6">
      <h1 className="contact-form__submit-result contact-form__submit-result__title contact-form__submit-result--center">
        {t('contactForm.thanksForYourRequest')}
      </h1>
      <h2
        className={clsx(
          'contact-form__submit-result contact-form__submit-result--center mt-6 mb-6',
          {
            'contact-form__submit-result--alarm': submitStatus !== ACTION_STATUS.SUCCESS,
          },
        )}
      >
        {submitStatus === ACTION_STATUS.SUCCESS
          ? t('contactForm.requestSuccessfully')
          : t('contactForm.yourRequestCouldNotBeSubmittedSuccessfully')}
      </h2>
      <div className="contact-form__submit-result__separator mt-6 mb-6" />
      <div className="contact-form__submit-result contact-form__submit-result--center fontBold mb-3">
        {t('checkout.confirmOrder.whatNext')}
      </div>
      <Row gutter={[24, 24]}>
        <Col lg={12} xs={24}>
          <CustomButton
            className="contact-form__button"
            type="primary"
            onClick={() => dispatch(contactActions.resetContact())}
          >
            {t('contactForm.makeAnotherRequest')}
          </CustomButton>
        </Col>
        <Col lg={12} xs={24}>
          <CustomButton
            className="contact-form__button"
            type="ghost"
            onClick={() => {
              navigate(linkGenerator('/my-workspace'))
            }}
          >
            {t('checkout.confirmOrder.myWorkspace')}
          </CustomButton>
        </Col>
      </Row>
    </div>
  )

  return submitStatus === ACTION_STATUS.DEFAULT
    ? renderSubmitForm()
    : renderSubmitResult()
}

export default ContactFormPage
